import KeycloakProvider from 'next-auth/providers/keycloak';

import { logger } from '@lichtblick/logger';

const stripQuotes = (value: string) => {
  const trimmed = value.trim();

  if (trimmed.startsWith('"') && trimmed.endsWith('"') && trimmed.length > 1) {
    return trimmed.slice(1, -1);
  }

  return trimmed;
};

const getNormalisedKeycloakIssuer = () => {
  const rawIssuer = process.env.KEYCLOAK_ISSUER ?? '';
  const sanitised = stripQuotes(rawIssuer);

  if (!sanitised) {
    logger.error('Keycloak issuer is empty at provider initialisation', {
      component: 'AuthService',
      operation: 'providerInit',
      hasRawIssuer: rawIssuer.length > 0,
    });

    return '';
  }

  try {
    const parsed = new URL(sanitised);

    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      logger.error('Keycloak issuer has unsupported protocol', {
        component: 'AuthService',
        operation: 'providerInit',
        protocol: parsed.protocol,
      });

      return '';
    }

    const withoutTrailingSlash = sanitised.replace(/\/+$/, '');

    logger.info('Resolved Keycloak issuer for NextAuth', {
      component: 'AuthService',
      operation: 'providerInit',
      issuerHost: parsed.host,
      issuerPath: parsed.pathname,
    });

    return withoutTrailingSlash;
  } catch (error) {
    logger.error('Keycloak issuer is not a valid absolute URL', {
      component: 'AuthService',
      operation: 'providerInit',
      issuerPreview: sanitised.slice(0, 80),
      error: error instanceof Error ? error.message : String(error),
    });

    return '';
  }
};

const keycloakIssuer = getNormalisedKeycloakIssuer();
const keycloakAuthorizationUrl = keycloakIssuer ? `${keycloakIssuer}/protocol/openid-connect/auth` : undefined;
const keycloakTokenUrl = keycloakIssuer ? `${keycloakIssuer}/protocol/openid-connect/token` : undefined;
const keycloakUserInfoUrl = keycloakIssuer ? `${keycloakIssuer}/protocol/openid-connect/userinfo` : undefined;
const keycloakWellKnownUrl = keycloakIssuer ? `${keycloakIssuer}/.well-known/openid-configuration` : undefined;

if (keycloakIssuer) {
  logger.info('Resolved Keycloak OAuth endpoints', {
    component: 'AuthService',
    operation: 'providerInit',
    authorizationUrl: keycloakAuthorizationUrl,
    tokenUrl: keycloakTokenUrl,
    userInfoUrl: keycloakUserInfoUrl,
    wellKnownUrl: keycloakWellKnownUrl,
  });
}

export const keycloakProvider = KeycloakProvider({
  clientId: process.env.KEYCLOAK_ID ?? '',
  clientSecret: process.env.KEYCLOAK_SECRET ?? '',
  issuer: keycloakIssuer,
  ...(keycloakAuthorizationUrl ? { authorization: keycloakAuthorizationUrl } : {}),
  ...(keycloakTokenUrl ? { token: keycloakTokenUrl } : {}),
  ...(keycloakUserInfoUrl ? { userinfo: keycloakUserInfoUrl } : {}),
  ...(keycloakWellKnownUrl ? { wellKnown: keycloakWellKnownUrl } : {}),
});

export const getKeycloakIssuer = () => keycloakIssuer;
